import {
  FlowNodeData,
  FlowNodeSetting,
  SettingParam,
  FlowMode,
  AssistantType,
  FlowNodeTypes,
} from "@/types/node";
import React, {useEffect, useState, useRef} from "react";
import {Node} from "@xyflow/react";
import {useNodeUpdater} from "../hooks/useNodeUpdater";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import {getSettingValueByKey} from "@/utils/index";
import {
  Settings,
  Settings2,
  Sparkles,
  Zap,
  CircleHelp,
  ListChecks,
  OctagonX,
  Hand,
  UserCog,
  BookOpen,
  OctagonAlert,
  Braces,
  ArrowDown,
  KeyRound,
  MessageCircle,
  Calendar,
  Play,
} from "lucide-react";
import {useSave} from "@/contexts/SaveContext";
import {CaptureVariableIcon} from "@/components/icons/CaptureVariableIcon";
import {SettingItemProps} from "@/components/settings/SettingMultiple";
import {SettingComponentMap} from "@/components/settings/index";
import {useValidation} from "@/utils/validation";
import {Button} from "@/components/ui/Button";
import {useDrawer} from "@/contexts/DrawerContext";
import {DRAWER_IDS} from "@/components/Drawers";
export const iconsMap = {
  Settings: Settings,
  Zap: Zap,
  Sparkles: Sparkles,
  Settings2: Settings2,
  CircleHelp: CircleHelp,
  ListChecks: ListChecks,
  OctagonX: OctagonX,
  Hand: Hand,
  UserCog: UserCog,
  BookOpen: BookOpen,
  OctagonAlert: OctagonAlert,
  Braces: Braces,
  KeyRound: KeyRound,
  MessageCircle: MessageCircle,
  ArrowDown: ArrowDown,
  Calendar: Calendar,
  CaptureVariableIcon: CaptureVariableIcon,
};

const hasDefinedValue = (
  obj: any,
  parentKey: string | null = null,
): boolean => {
  // See if the setting has any defined value property nested in any level
  // except for selectOptions.values
  if (Array.isArray(obj)) {
    return obj.some(item => hasDefinedValue(item, parentKey));
  }

  if (obj && typeof obj === "object") {
    for (const [key, val] of Object.entries(obj)) {
      if (
        key === "value" &&
        parentKey !== "selectOptions" &&
        val !== undefined &&
        val !== "" &&
        val !== false &&
        !(
          typeof val === "object" &&
          val !== null &&
          Object.keys(val).length === 0
        )
      ) {
        return true;
      }

      if (hasDefinedValue(val, key)) return true;
    }
  }

  return false;
};

export const DynamicIcon = ({
  iconName,
  className,
}: {
  iconName: keyof typeof iconsMap;
  className: string;
}) => {
  const IconComponent = iconsMap[iconName];
  return IconComponent ? <IconComponent className={className} /> : null;
};

export const RenderSetting = ({
  param,
  index,
  selectedNode,
  assistantType,
  handleChange,
  saveOnChange = true,
  handleSave,
  flowMode,
  handleSaveAction,
}: {
  param: SettingParam;
  index: number;
  selectedNode: Node;
  assistantType: AssistantType;
  handleChange: (param: SettingParam, value: any) => void;
  saveOnChange?: boolean;
  handleSave: (afterSave?: () => void) => void;
  flowMode: FlowMode;
  handleSaveAction: (actionId: string, param: SettingParam, value: any) => void;
}) => {
  const isActionNode =
    selectedNode?.data.type === FlowNodeTypes.ACTION ||
    selectedNode?.data.type === FlowNodeTypes.TRANSFER ||
    selectedNode?.data.type === FlowNodeTypes.BOOKING;
  const SettingComponent = SettingComponentMap[param.type];
  const {error, validate} = useValidation(param.validationSchema as any);

  return SettingComponent && !param.hidden ? (
    <SettingComponent
      key={param.key}
      param={param}
      index={index}
      selectedNodeId={selectedNode.id}
      selectedNode={selectedNode}
      error={error}
      validate={validate}
      handleChange={(param: SettingParam, value: any) => {
        validate(value);
        handleChange(param, value);
        if (saveOnChange) {
          if (isActionNode) {
            const actionData = getSettingValueByKey(
              selectedNode.data.settings as FlowNodeSetting[],
              "action",
            ) as Record<string, any>;
            if (!actionData?.action_id) {
              return;
            }
            handleSave(() =>
              handleSaveAction(actionData?.action_id, param, value),
            );
          } else {
            handleSave();
          }
        }
      }}
      selectOptions={param.selectOptions || []}
      placeholder={param.placeholder}
      disabled={flowMode === FlowMode.LIVE}
      assistantType={assistantType}
      className={param.className}
      tooltipMessage={param.tooltipMessage}
    />
  ) : null;
};

const SettingsGroup = ({
  settings,
  selectedNode,
  assistantType,
  takeSnapshot,
  updateNodeSettingParam,
  onSettingChange,
  saveOnChange = true,
}: {
  settings: FlowNodeSetting[];
  selectedNode: Node;
  assistantType: AssistantType;
  takeSnapshot: () => void;
  updateNodeSettingParam: (id: string, key: string, value: any) => void;
  onSettingChange?: (param: SettingParam, value: SettingItemProps[]) => void;
  saveOnChange?: boolean;
}) => {
  const {handleSave, handleSaveAction, flowMode} = useSave();

  return (
    <>
      {settings.map((setting, index) => (
        <div key={index}>
          {setting.params.map((param, i) => (
            <RenderSetting
              key={param.key}
              param={param}
              index={i}
              selectedNode={selectedNode}
              assistantType={assistantType}
              handleChange={(param, value) => {
                updateNodeSettingParam(selectedNode.id, param.key, value);
                onSettingChange?.(param, value);
                takeSnapshot();
              }}
              saveOnChange={saveOnChange}
              handleSave={handleSave}
              flowMode={flowMode}
              handleSaveAction={handleSaveAction}
            />
          ))}
        </div>
      ))}
    </>
  );
};

export const NodeSettings: React.FC<{
  selectedNode: Node;
  takeSnapshot: () => void;
  assistantType: AssistantType;
  onSettingChange?: (param: SettingParam, value: SettingItemProps[]) => void;
  saveOnChange?: boolean;
}> = React.memo(
  ({
    selectedNode,
    takeSnapshot,
    assistantType,
    onSettingChange,
    saveOnChange,
  }) => {
    const settingsRef = useRef(null);
    const [openedIndex, setOpenedIndex] = useState(0);
    const isCustomAction = selectedNode.data.type === FlowNodeTypes.ACTION;
    // This is a hack to override the overflow-hidden class on the first accordion item
    // on first render, to allow dropdowns inside accordions to be visible
    const [firstOpenOverrideActive, setFirstOpenOverrideActive] =
      useState(true);
    const {updateNodeSettingParam} = useNodeUpdater();
    const {openDrawer} = useDrawer();

    const nodeData = selectedNode.data as FlowNodeData;
    const settingsTitle =
      getSettingValueByKey(nodeData.settings ?? [], "name") ||
      nodeData.settingsTitle ||
      "New Stage";

    const settings = nodeData.settings as FlowNodeSetting[];

    useEffect(() => {
      // Autofocus first field in settings
      if (settingsRef.current && openedIndex !== null) {
        const accordionItem = settingsRef.current.querySelector(
          `[data-accordion-index="${openedIndex}"]`,
        );
        const settingWrapperRef = accordionItem ?? settingsRef.current;
        const firstField = settingWrapperRef?.querySelector(
          "input, select, textarea, button, div[contenteditable]",
        );

        if (firstField) firstField.focus();
        setOpenedIndex(null);
      }
    }, [openedIndex]);

    return (
      <div
        ref={settingsRef}
        className="flex flex-col max-h-full h-full"
        key={selectedNode.id}
      >
        <h2 className="text-lg font-semibold border-b p-5 text-ellipsis overflow-hidden whitespace-nowrap flex justify-between items-center">
          <span className="truncate mr-4">
            {settingsTitle?.toString() || "New Stage"}
          </span>

          {isCustomAction && (
            <Button
              className="[&_svg]:size-5"
              onClick={() => {
                console.log("Initialize Action");
                openDrawer(DRAWER_IDS.INITIALIZE_ACTION);
              }}
            >
              <Play className="w-4.5 h-4.5 text-white" /> Initialize
            </Button>
          )}
        </h2>
        <div className="mb-3 overflow-y-auto flex-grow">
          {settings.length === 1 ? (
            <>
              {nodeData.settings && nodeData.settings[0].description && (
                <p className="text-secondary px-5 text-sm mt-3">
                  {nodeData.settings[0].description}
                </p>
              )}
              <div className="p-5">
                <SettingsGroup
                  settings={settings}
                  selectedNode={selectedNode}
                  assistantType={assistantType}
                  takeSnapshot={takeSnapshot}
                  updateNodeSettingParam={updateNodeSettingParam}
                  onSettingChange={onSettingChange}
                  saveOnChange={saveOnChange}
                />
              </div>
            </>
          ) : (
            settings.map((setting, index) => {
              const hasDefinedParam = hasDefinedValue(setting);
              const isExpanded = setting.expandedSetting;
              return !isExpanded ? (
                <Accordion
                  type="single"
                  collapsible
                  key={index}
                  defaultValue={`item-0`}
                >
                  <AccordionItem
                    value={`item-${index}`}
                    className="overflow-visible"
                  >
                    <AccordionTrigger
                      onClick={() => {
                        setOpenedIndex(index);
                        if (index === 0 && firstOpenOverrideActive) {
                          setFirstOpenOverrideActive(false);
                        }
                      }}
                    >
                      {setting.icon && (
                        <DynamicIcon
                          iconName={setting.icon}
                          className={`mr-2 h-5 w-5 ${
                            hasDefinedParam
                              ? "text-primary-accent"
                              : "text-secondary"
                          }`}
                        />
                      )}
                      <h4 className="font-semibold">{setting.title}</h4>
                    </AccordionTrigger>
                    <AccordionContent
                      data-accordion-index={index}
                      parentClassName={`p-0 ${
                        firstOpenOverrideActive && index === 0
                          ? "!overflow-visible"
                          : ""
                      }`}
                    >
                      {setting.description && (
                        <p className="mb-3 text-secondary">
                          {setting.description}
                        </p>
                      )}
                      <div>
                        <SettingsGroup
                          settings={[setting]}
                          selectedNode={selectedNode}
                          assistantType={assistantType}
                          takeSnapshot={takeSnapshot}
                          updateNodeSettingParam={updateNodeSettingParam}
                          onSettingChange={onSettingChange}
                          saveOnChange={saveOnChange}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : (
                <div className="p-5 border-b" key={index}>
                  <SettingsGroup
                    settings={[setting]}
                    selectedNode={selectedNode}
                    assistantType={assistantType}
                    takeSnapshot={takeSnapshot}
                    updateNodeSettingParam={updateNodeSettingParam}
                    onSettingChange={onSettingChange}
                    saveOnChange={saveOnChange}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  },
);

NodeSettings.displayName = "NodeSettings";
